import { Spin } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tab, Nav } from "react-bootstrap";
import { pifGet, pifPost } from "../../../helpers/api_helper";
import {
  GET_PIF_MasterGaps,
  POST_PIF_MasterPush,
  POST_PIF_MasterOverride,
  POST_PIF_ReconcileMasters,
} from "../../../helpers/url_helper";
import { successMessage, errorMessage, customConfirmDialog } from "../CommonFunctions";
import { pifDateTime } from "./pifConstants";

// 0 not pushed · 1 pending · 2 confirmed · 3 drifted
const MIRROR = {
  0: { label: "Not in Tally", badge: "danger", icon: "fa-circle-xmark" },
  1: { label: "Queued", badge: "warning", icon: "fa-clock" },
  2: { label: "In Tally", badge: "success", icon: "fa-circle-check" },
  3: { label: "Drifted", badge: "warning", icon: "fa-triangle-exclamation" },
};

const TABS = [
  { key: "customers", type: 1, label: "Customers", noun: "customer", detailLabel: "GSTIN" },
  { key: "tests", type: 2, label: "Tests", noun: "test", detailLabel: "" },
  { key: "expenses", type: 4, label: "Expenses", noun: "expense", detailLabel: "" },
  { key: "units", type: 3, label: "Units", noun: "unit", detailLabel: "Symbol" },
];

const MasterGaps = () => {
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const companyIdEnc = AuthUser?.CompanyIDEncrypt;
  const formsList = AuthUser?.Permissions?.FormList;
  const canPush =
    formsList?.filter((x) => x?.FormID == 112 && (x?.IsAdd || x?.IsEdit))?.length > 0;
  const enc = encodeURIComponent(companyIdEnc);

  const [isLoading, setIsLoading] = useState(false);
  const [onlyGaps, setOnlyGaps] = useState(true);
  const [data, setData] = useState({}); // type -> rows
  const [editing, setEditing] = useState(null); // { key, value } of the row being edited
  const [savedKey, setSavedKey] = useState(null); // SourceKey that just saved (flash a tick)
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("customers");

  const fetchType = useCallback(
    (type) =>
      pifGet(
        `${GET_PIF_MasterGaps}?companyIdEnc=${enc}&masterType=${type}&onlyGaps=${onlyGaps}`
      ),
    [enc, onlyGaps]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const results = await Promise.all(TABS.map((t) => fetchType(t.type)));
      const next = {};
      TABS.forEach((t, i) => (next[t.type] = results[i] || []));
      setData(next);
      setEditing(null);
    } catch {
      errorMessage("Could not load master gaps.");
    }
    setIsLoading(false);
  }, [fetchType]);

  const reloadType = async (type) => {
    try {
      const rows = await fetchType(type);
      setData((d) => ({ ...d, [type]: rows || [] }));
    } catch {
      /* keep the current view */
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Commit the inline Tally-name edit (Enter / blur). Only POSTs if it changed.
  const commitEdit = (masterType, r) => {
    if (!editing || editing.key !== r.SourceKey) return;
    const val = (editing.value || "").trim();
    setEditing(null);
    if (val === (r.TallyNameOverride || "").trim()) return;
    pifPost(POST_PIF_MasterOverride, {
      CompanyIdEnc: companyIdEnc,
      MasterType: masterType,
      SourceId: r.SourceId,
      TallyName: val || null,
    })
      .then((res) => {
        if (res?.IsSuccess) {
          successMessage(res?.Message || "Tally name saved.");
          setSavedKey(r.SourceKey);
          setTimeout(() => setSavedKey((k) => (k === r.SourceKey ? null : k)), 1800);
          reloadType(masterType);
        } else {
          errorMessage(res?.Message || "Could not save the Tally name.");
        }
      })
      .catch(() => errorMessage("Could not reach the PIF service."));
  };

  const pushMasters = () => {
    customConfirmDialog(
      "Push masters to Tally",
      "Queue a job that creates every missing customer, test, expense item and unit in Tally. The agent runs it on its next poll.",
      async () => {
        setIsLoading(true);
        try {
          const res = await pifPost(POST_PIF_MasterPush, { CompanyIdEnc: companyIdEnc });
          res?.IsSuccess
            ? successMessage(res?.Message || "Master push queued.")
            : errorMessage(res?.Message || "Could not queue.");
        } catch {
          errorMessage("Could not queue the master push.");
        }
        setIsLoading(false);
        load();
      },
      true
    );
  };

  const reconcile = () => {
    customConfirmDialog(
      "Reconcile with Tally",
      "Reads Tally's current customers, items and units and marks the ones that already exist as linked — no changes are made in Tally. Use this after a data reset, or when a sync fails with \"cannot alter units\". The agent runs it on its next poll.",
      async () => {
        setIsLoading(true);
        try {
          const res = await pifPost(POST_PIF_ReconcileMasters, { CompanyIdEnc: companyIdEnc });
          res?.IsSuccess
            ? successMessage(res?.Message || "Reconcile queued.")
            : errorMessage(res?.Message || "Could not queue the reconcile.");
        } catch {
          errorMessage("Could not queue the reconcile.");
        }
        setIsLoading(false);
      },
      true
    );
  };

  const totals = useMemo(() => {
    const t = { confirmed: 0, queued: 0, missing: 0, drifted: 0 };
    Object.values(data).forEach((rows) =>
      (rows || []).forEach((r) => {
        if (r.MirrorStatus === 2) t.confirmed++;
        else if (r.MirrorStatus === 1) t.queued++;
        else if (r.MirrorStatus === 3) t.drifted++;
        else t.missing++;
      })
    );
    return t;
  }, [data]);

  const gapCount = (type) =>
    (data[type] || []).filter((r) => r.MirrorStatus !== 2).length;

  const visibleRows = (type) => {
    const q = search.trim().toLowerCase();
    const rows = data[type] || [];
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.DisplayName || "").toLowerCase().includes(q) ||
        (r.TallyNameOverride || "").toLowerCase().includes(q) ||
        (r.Detail || "").toLowerCase().includes(q)
    );
  };

  const chip = (n, label, tone) =>
    n > 0 && (
      <span className={`badge badge-${tone} light me-2 mb-1`}>
        {n} {label}
      </span>
    );

  return (
    <div className="m-1">
      <style>{`
        .mg-scroll { max-height: calc(100vh - 320px); overflow: auto; }
        /* sticky header — same colour as every other list header
           (--rgba-primary-2 over the card); the opaque base stops rows showing through */
        .mg-scroll table thead tr th {
          position: sticky;
          top: 0;
          z-index: 3;
          background-color: #fff;
          background-image: linear-gradient(var(--rgba-primary-2), var(--rgba-primary-2));
        }
        [data-theme-version="dark"] .mg-scroll table thead tr th {
          background-color: #1E1E1E;
          background-image: none;
        }
      `}</style>
      <Spin size="large" spinning={isLoading} tip="Loading..." style={{ position: "fixed" }}>
        {/* ---- header ---- */}
        <div className="card mb-2">
          <div className="card-body p-2 d-flex flex-wrap align-items-center justify-content-between">
            <div>
              <h4 className="heading mb-0">Master Gaps</h4>
              <small className="text-muted">
                Every customer, test, expense and unit PIF creates in Tally. Set a{" "}
                <em>Tally name</em> to point at a master that already exists there instead of
                creating a new one.
              </small>
            </div>
            <div className="d-flex align-items-center flex-wrap">
              <label className="form-check form-switch me-3 mb-0">
                <input
                  className="form-check-input me-1"
                  type="checkbox"
                  checked={onlyGaps}
                  onChange={(e) => setOnlyGaps(e.target.checked)}
                />
                <span className="form-check-label">Only show gaps</span>
              </label>
              <button className="btn btn-outline-primary btn-sm me-2" onClick={load}>
                <i className="fa fa-refresh me-1" /> Refresh
              </button>
              {canPush && (
                <>
                  <button
                    className="btn btn-outline-secondary btn-sm me-2"
                    onClick={reconcile}
                    title="Match against what already exists in Tally (read-only)"
                  >
                    <i className="fa fa-link me-1" /> Reconcile with Tally
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={pushMasters}>
                    <i className="fa fa-cloud-upload-alt me-1" /> Push all to Tally
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ---- summary strip ---- */}
        <div className="card mb-2">
          <div className="card-body p-2 d-flex flex-wrap align-items-center">
            <span className="text-muted me-3 mb-1">Across all masters:</span>
            {chip(totals.confirmed, "in Tally", "success")}
            {chip(totals.queued, "queued", "warning")}
            {chip(totals.drifted, "drifted", "warning")}
            {chip(totals.missing, "not in Tally", "danger")}
            {totals.confirmed + totals.queued + totals.drifted + totals.missing === 0 && (
              <span className="text-muted mb-1">Nothing to show.</span>
            )}
          </div>
        </div>

        {/* ---- tabs + tables ---- */}
        <div className="card">
          <div className="card-body p-2">
            <div className="default-tab">
              <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                <div className="d-flex flex-wrap align-items-center justify-content-between">
                  <Nav as="ul" className="nav-tabs border-0">
                    {TABS.map((t) => {
                      const g = gapCount(t.type);
                      return (
                        <Nav.Item as="li" key={t.key}>
                          <Nav.Link eventKey={t.key}>
                            {t.label}
                            <span
                              className={`badge light ms-2 badge-${g > 0 ? "danger" : "success"}`}
                            >
                              {g > 0 ? g : <i className="fa fa-check" />}
                            </span>
                          </Nav.Link>
                        </Nav.Item>
                      );
                    })}
                  </Nav>
                  <div className="mb-1" style={{ minWidth: 220 }}>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text">
                        <i className="fa fa-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search this tab…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <Tab.Content className="pt-2">
                  {TABS.map((t) => {
                    const editable = t.type !== 3; // units are mapped on the Config screen
                    const rows = visibleRows(t.type);
                    return (
                      <Tab.Pane eventKey={t.key} key={t.key}>
                        <div className="table-responsive active-projects style-1 shorting mg-scroll">
                          <table className="table dataTable no-footer mb-0">
                            <thead>
                              <tr>
                                <th>{t.label.replace(/s$/, "")}</th>
                                {t.detailLabel && <th>{t.detailLabel}</th>}
                                {editable && (
                                  <th>
                                    Tally name
                                    <span className="text-muted fs-12 ms-1">
                                      — click to link an existing one
                                    </span>
                                  </th>
                                )}
                                <th>Status</th>
                                <th>Last verified</th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              {rows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={4 + (t.detailLabel ? 1 : 0) + (editable ? 1 : 0)}
                                    className="text-center text-muted py-3"
                                  >
                                    {search.trim() ? (
                                      "No match."
                                    ) : onlyGaps ? (
                                      <>
                                        <i className="fa fa-circle-check text-success me-1" />
                                        Every {t.noun} is in Tally.
                                      </>
                                    ) : (
                                      "Nothing to show."
                                    )}
                                  </td>
                                </tr>
                              ) : (
                                rows.map((r) => {
                                  const m = MIRROR[r.MirrorStatus] || MIRROR[0];
                                  const missing = r.MirrorStatus !== 2;
                                  return (
                                    <tr
                                      key={r.SourceKey}
                                      style={
                                        missing
                                          ? { boxShadow: "inset 3px 0 0 var(--bs-danger)" }
                                          : undefined
                                      }
                                    >
                                      <td>
                                        <div>{r.DisplayName}</div>
                                        {r.TallyName &&
                                          r.TallyName !== r.DisplayName &&
                                          r.TallyName !== r.TallyNameOverride && (
                                            <small className="text-muted">
                                              in Tally as “{r.TallyName}”
                                            </small>
                                          )}
                                      </td>
                                      {t.detailLabel && (
                                        <td className="text-muted">
                                          {r.Detail || "—"}
                                        </td>
                                      )}
                                      {editable && (
                                        <td>
                                          {editing?.key === r.SourceKey ? (
                                            <input
                                              type="text"
                                              className="form-control form-control-sm"
                                              autoFocus
                                              placeholder={`Tally name for “${r.DisplayName}”`}
                                              value={editing.value}
                                              onChange={(e) =>
                                                setEditing({
                                                  key: r.SourceKey,
                                                  value: e.target.value,
                                                })
                                              }
                                              onBlur={() => commitEdit(t.type, r)}
                                              onKeyDown={(e) => {
                                                if (e.key === "Enter") e.target.blur();
                                                if (e.key === "Escape") setEditing(null);
                                              }}
                                            />
                                          ) : (
                                            <span
                                              role={canPush ? "button" : undefined}
                                              className={
                                                r.TallyNameOverride
                                                  ? "d-inline-block"
                                                  : "text-muted fst-italic d-inline-block"
                                              }
                                              title={canPush ? "Click to edit" : undefined}
                                              style={
                                                canPush
                                                  ? {
                                                      borderBottom: "1px dashed var(--bs-border-color)",
                                                      cursor: "text",
                                                    }
                                                  : undefined
                                              }
                                              onClick={() =>
                                                canPush &&
                                                setEditing({
                                                  key: r.SourceKey,
                                                  value: r.TallyNameOverride || "",
                                                })
                                              }
                                            >
                                              {r.TallyNameOverride || "same as LIMS"}
                                              {savedKey === r.SourceKey && (
                                                <i className="fa fa-circle-check text-success ms-2" />
                                              )}
                                            </span>
                                          )}
                                        </td>
                                      )}
                                      <td>
                                        <span className={`badge badge-${m.badge} light`}>
                                          <i className={`fa ${m.icon} me-1`} />
                                          {m.label}
                                        </span>
                                      </td>
                                      <td className="text-muted fs-12">
                                        {r.MirrorStatus === 2 || r.MirrorStatus === 3
                                          ? pifDateTime(r.LastVerifiedUtc || r.LastPushedUtc)
                                          : "—"}
                                      </td>
                                      <td />
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Tab.Pane>
                    );
                  })}
                </Tab.Content>
              </Tab.Container>
            </div>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default MasterGaps;
